import config from '../../config';
import { computeAbBucket } from '../abBucket';
import { hasCurrentUserValidAnnualPass, isFlexPass } from '../annualPass';
import { CHECKOUT_SOURCE, isCheckoutCampaign } from '../checkoutContext';
import applyConsent from '../consent/applyConsent';
import { getConsent, onConsentChange } from '../consent/consentState';
import { logError } from '../log';
import { requestLocationPermission } from '../maps';
import { PURCHASABLE_PASS_TYPES } from '../passUpgrade';
import { resolveAppLocale } from './appLocale';
import { getTrackingStatus, requestTrackingPermission } from './appTracking';
import { isAndroid, isCordova, isIOS } from './deviceDetection';
// ================ Action types ================ //

// ================ Reducer ================ //

const initialState = {
  oneSignalTagsSet: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type } = action;
  switch (type) {
    default:
      return state;
  }
}

// ================ Selectors ================ //

const setOneSignalDebugging = () => {
  // Uncomment to set OneSignal device logging to VERBOSE
  // const OneSignal = window.plugins.OneSignal;
  // OneSignal.Debug.setLogLevel(6);
  // OneSignal.Debug.setAlertLevel(6);
};

// OneSignal attaches its Notifications namespace asynchronously after
// `initialize()`. If a caller hits pushNotificationTrigger before that
// happens we shouldn't permanently skip the permission prompt — wait a
// little and try again. Caps at ~3s total.
const ONE_SIGNAL_READY_MAX_RETRIES = 6;
const ONE_SIGNAL_READY_RETRY_MS = 500;

export const pushNotificationTrigger = async (attempt = 0) => {
  const notifications = window?.plugins?.OneSignal?.Notifications;
  if (!notifications) {
    if (attempt < ONE_SIGNAL_READY_MAX_RETRIES) {
      setTimeout(() => pushNotificationTrigger(attempt + 1), ONE_SIGNAL_READY_RETRY_MS);
    }
    return;
  }

  let hasPushPermission;
  if (isAndroid()) {
    hasPushPermission = await notifications.getPermissionAsync();
  } else if (isIOS()) {
    const res = await notifications.permissionNative();
    hasPushPermission = !(res === 0 || res === 1);
  }
  if (!hasPushPermission) {
    oneSignalAddTrigger('push-notification-permission-prompt', 'true');
  }
};

// OneSignal sizes in-app messages (and with them the push-permission prompt) to the
// SCREEN, not the app window. In an iPadOS window smaller than the screen they are
// clipped and their close button is out of reach, so they are paused while the window
// is smaller than the screen.
const WINDOW_WIDTH_TOLERANCE = 2;
// The web view starts below the 32 pt status band (24-40 pt by device, +20 pt during a
// call), so only the height gets a generous tolerance; a windowed iPad is short by far more.
const WINDOW_HEIGHT_TOLERANCE = 64;
// Windows only exist on iPad; this keeps iPhones (taller status bar) out of the check.
const WINDOWED_MIN_SCREEN_SHORT_SIDE = 600;
const WINDOW_RESIZE_DEBOUNCE_MS = 250;

/**
 * @param {{ innerWidth: number, innerHeight: number, screenWidth: number, screenHeight: number }} sizes
 * @returns {boolean} true when the app window is clearly smaller than the screen
 */
export const isWindowSmallerThanScreen = ({ innerWidth, innerHeight, screenWidth, screenHeight }) => {
  const long = Math.max(screenWidth, screenHeight);
  const short = Math.min(screenWidth, screenHeight);
  if (short < WINDOWED_MIN_SCREEN_SHORT_SIDE) return false;
  // iOS WebKit reports screen.width/height regardless of orientation: match them to the window's.
  const landscape = innerWidth > innerHeight;
  const expectedWidth = landscape ? long : short;
  const expectedHeight = landscape ? short : long;
  return (
    innerWidth < expectedWidth - WINDOW_WIDTH_TOLERANCE ||
    innerHeight < expectedHeight - WINDOW_HEIGHT_TOLERANCE
  );
};

let inAppMessagesPausedForWindow = false;
let windowResizeListenerRegistered = false;
let windowResizeTimer = null;

const isCurrentWindowSmallerThanScreen = () =>
  isWindowSmallerThanScreen({
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  });

const retryInAppMessagePauseSync = (attempt) => {
  if (attempt >= ONE_SIGNAL_READY_MAX_RETRIES) return;
  setTimeout(() => syncInAppMessagePause(attempt + 1), ONE_SIGNAL_READY_RETRY_MS);
};

// Only ever resumes what this function paused, so nothing else that pauses stays overridden
// and iPhone / full-screen iPad never see a setPaused call.
export const syncInAppMessagePause = (attempt = 0) => {
  if (!isCordova() || !isIOS()) return;
  const inAppMessages = window.plugins?.OneSignal?.InAppMessages;
  if (!inAppMessages) return retryInAppMessagePauseSync(attempt);
  const smaller = isCurrentWindowSmallerThanScreen();
  if (smaller === inAppMessagesPausedForWindow) return;
  inAppMessagesPausedForWindow = smaller;
  console.log(`[oneSignal] in-app messages paused for small window: ${smaller}`);
  inAppMessages.setPaused(smaller);
};

// The SDK may drop setPaused while privacy consent is missing (the first sync runs at init,
// before consent), and the flag above would then never re-send it: repeat it once consent is in.
export const reapplyInAppMessagePause = () => {
  if (!isCordova() || !isIOS() || !inAppMessagesPausedForWindow) return;
  window.plugins?.OneSignal?.InAppMessages?.setPaused(true);
};

const watchWindowSize = () => {
  if (!isCordova() || !isIOS()) return;
  if (!windowResizeListenerRegistered) {
    windowResizeListenerRegistered = true;
    window.addEventListener('resize', () => {
      clearTimeout(windowResizeTimer);
      windowResizeTimer = setTimeout(() => syncInAppMessagePause(), WINDOW_RESIZE_DEBOUNCE_MS);
    });
  }
  syncInAppMessagePause();
};

// ================ Action creators ================ //

// ================ Thunks ================ //

// OneSignal must stay silent until the cookie-consent startup screen is
// answered, or in-app messages (onboarding, push prompt, campaigns) pop up on
// top of it. "Answered" means any decision — declining cookies keeps push
// functional; its own OS-level opt-in governs delivery.
let oneSignalConsentGranted = false;
let oneSignalConsentListenerRegistered = false;

export const oneSignalMaybeGrantConsent = async () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  if (oneSignalConsentGranted) return;
  if (!getConsent()) return;
  oneSignalConsentGranted = true;
  window.plugins.OneSignal.setConsentGiven(true);
  flushCapabilityTags();
  syncConsentTags();
  reapplyInAppMessagePause();
  oneSignalInitServices();
  // Seed the campaign-segmentation tag once consent is given; earlier the SDK drops it.
  try {
    const tags = await window.plugins.OneSignal.User.getTags();
    if (!tags || Object.keys(tags).length === 0) {
      oneSignalAddTags({ registered: false });
    }
  } catch (e) {
    // Tags are segmentation sugar — never fail the consent grant over them.
  }
};

export const oneSignalInit = () => {
  try {
    if (!isCordova() || !window?.plugins?.OneSignal) return;
    console.log('[oneSignal] init');
    setOneSignalDebugging();

    window.plugins.OneSignal.initialize(config.oneSignal.appId);
    window.plugins.OneSignal.setConsentRequired(true);
    watchWindowSize();
    refreshAttStatus().catch((e) => logError(e, 'one-signal-consent-tags'));

    if (!oneSignalConsentListenerRegistered) {
      oneSignalConsentListenerRegistered = true;
      // The first decision grants (and tags); every later change in the settings re-tags.
      onConsentChange(() =>
        oneSignalConsentGranted ? syncConsentTags() : oneSignalMaybeGrantConsent(),
      );
    }
    oneSignalMaybeGrantConsent();
  } catch (e) {
    logError(e, 'one-signal-init', { message: 'Failed to initialize OneSignal' });
  }
};

export const oneSignalInitServices = () => {
  const locale = navigator.language.split('-')[0];
  oneSignalSharedLocation();
  oneSignalSetLanguage(locale);
  oneSignalAddObservers();
  registerIamClickListener();
  pushNotificationTrigger();
};

export const oneSignalSharedLocation = async () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  const isShared = await window.plugins.OneSignal.Location.isShared();
  window.plugins.OneSignal.Location.setShared(isShared);
};

export const oneSignalSetLanguage = (locale) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.User.setLanguage(locale);
};


export const IAM_BUY_PASS_PREFIX = 'buy-pass:';

/**
 * Reads the pass type from an in-app message button's Custom Action ID
 * (`buy-pass:<passType>`). Anything else, including unknown pass types, is null.
 */
export const parseIamAction = (actionId) => {
  if (typeof actionId !== 'string' || !actionId.startsWith(IAM_BUY_PASS_PREFIX)) return null;
  const passType = actionId.slice(IAM_BUY_PASS_PREFIX.length);
  return PURCHASABLE_PASS_TYPES.includes(passType) ? { passType } : null;
};

// No settings fallback: after a denial the message just moves on.
const promptPush = () => window.plugins?.OneSignal?.Notifications?.requestPermission(false);

// ATT exists on iOS only; the message hides this step on Android. Without marketing consent
// ATT has no use, and iOS asks only once: the prompt is kept for a later consent change in
// the cookie settings. Marketing vendors boot only once ATT is granted, so the stored consent
// is re-applied to start them without a restart.
const promptTracking = async () => {
  if (!isIOS()) return;
  const consent = getConsent();
  if (!consent?.marketing) return;
  const granted = await requestTrackingPermission();
  syncConsentTags();
  if (granted) applyConsent(consent);
};

// Apple and Google decide themselves whether the review dialog shows (quota) and report nothing
// back. When the dialog is unavailable the store page opens with its review form instead.
const promptReview = async () => {
  const appReview = window.cordova?.plugins?.AppReview;
  if (!appReview) return;
  try {
    await appReview.requestReview();
  } catch (e) {
    await appReview.openStoreScreen(null, true);
  }
};

// Store page with its review form. Apple and Google advise this for a button: the native dialog
// may not show (quota), which leaves the button without effect.
const openStoreReview = () => window.cordova?.plugins?.AppReview?.openStoreScreen(null, true);

/**
 * Native dialogs (permissions, app review, store page) a button opens with `addClickName` (without `close`):
 * the message stays open and moves on by itself. OneSignal's own
 * `triggerPushPrompt`/`triggerLocationPrompt` close the message on Android, which ends a
 * multi-step onboarding after its first step.
 */
const IAM_PERMISSION_PROMPTS = new Map([
  ['open:store-review', openStoreReview],
  ['prompt:location', requestLocationPermission],
  ['prompt:push', promptPush],
  ['prompt:review', promptReview],
  ['prompt:tracking', promptTracking],
]);

/** App pages a button opens, as the route path below the locale. */
const IAM_PAGES = new Map([['open:signup', 'signup']]);

const navigateFromIam = (location, logData) => {
  if (!window.routerNavigate) {
    logError(new Error('routerNavigate missing'), 'one-signal-iam-click', logData);
    return;
  }
  window.routerNavigate(location);
};

// Never rejects; the click listener does not wait for the dialog.
const runIamPrompt = async (actionId, prompt) => {
  try {
    await prompt();
  } catch (e) {
    logError(e, 'one-signal-iam-prompt', { actionId });
  }
};

const handleBuyPassClick = (action, event) => {
  const params = new URLSearchParams({
    buy: action.passType,
    source: CHECKOUT_SOURCE.ONESIGNAL_IAM,
  });
  const messageId = event?.message?.messageId;
  if (isCheckoutCampaign(messageId)) params.set('campaign', messageId);

  navigateFromIam(
    { pathname: `/${resolveAppLocale()}/account/pass`, search: `?${params.toString()}` },
    { passType: action.passType },
  );
};

export const handleIamClick = (event) => {
  const actionId = event?.result?.actionId;
  if (IAM_PERMISSION_PROMPTS.has(actionId)) {
    runIamPrompt(actionId, IAM_PERMISSION_PROMPTS.get(actionId));
    return;
  }
  if (IAM_PAGES.has(actionId)) {
    navigateFromIam({ pathname: `/${resolveAppLocale()}/${IAM_PAGES.get(actionId)}` }, { actionId });
    return;
  }
  const action = parseIamAction(actionId);
  if (action) handleBuyPassClick(action, event);
};

export const oneSignalAddObservers = () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  // The OneSignal plugin attaches its sub-namespaces (Notifications, User,
  // InAppMessages) asynchronously after `initialize()`. If observers are
  // attached before they're ready, the dereferences below throw — see
  // VANSITE-FRONTEND-484/48H/495.
  const notifications = window.plugins.OneSignal.Notifications;
  const user = window.plugins.OneSignal.User;
  if (!notifications || !user?.pushSubscription) return;

  notifications.addEventListener('permissionChange', (granted) => {
    console.log('[oneSignal] permission changed:');
    console.log(granted);
    if (!granted && window.plugins.OneSignal.InAppMessages) {
      window.plugins.OneSignal.InAppMessages.addTrigger(
        'push-notification-permission-prompt',
        'true',
      );
    }
  });

  notifications.addEventListener('click', (jsonData) => {
    const notificationData = JSON.stringify(jsonData);
    console.log('[oneSignal] notification opened:');
    console.dir(notificationData);
  });

  user.pushSubscription.addEventListener('change', (event) => {
    console.log('[oneSignal] subscription changed');
    console.dir(event);
  });
};

let iamClickListenerRegistered = false;

// Registered on its own, not inside oneSignalAddObservers: that one bails out
// for good when Notifications/User are not attached yet, and InAppMessages
// attaches just as late. Retry until the namespace is there.
export const registerIamClickListener = (attempt = 0) => {
  if (!isCordova() || iamClickListenerRegistered) return;
  const inAppMessages = window?.plugins?.OneSignal?.InAppMessages;
  if (!inAppMessages) {
    if (attempt < ONE_SIGNAL_READY_MAX_RETRIES) {
      setTimeout(() => registerIamClickListener(attempt + 1), ONE_SIGNAL_READY_RETRY_MS);
    }
    return;
  }
  try {
    inAppMessages.addEventListener('click', handleIamClick);
    iamClickListenerRegistered = true;
    // Messages using these clicks trigger on `iam_actions >= n`, so builds without the listener
    // never show them. A tag cannot do that for first-session users: it arrives too late.
    inAppMessages.addTrigger(IAM_ACTIONS_TRIGGER, String(IAM_ACTIONS_VERSION));
  } catch (e) {
    // Must not abort oneSignalInitServices; the flag stays unset so a later call registers again.
    logError(e, 'one-signal-iam-listener');
  }
};

export const oneSignalAddTrigger = (key, value) => {
  // InAppMessages is attached asynchronously after initialize() — guard it the
  // same way oneSignalAddObservers does so a too-early call no-ops instead of
  // dereferencing an undefined namespace (VANSITE-FRONTEND-4FW).
  if (!isCordova() || !window?.plugins?.OneSignal?.InAppMessages) return;
  window.plugins.OneSignal.InAppMessages.addTrigger(key, value);
};

/**
 * Triggers for the in-app message after a guest's review. The review count goes in as a trigger
 * too: OneSignal evaluates audiences (segments/tags) only at session start, so a "first review"
 * segment would not match in the session where the review is written. One call, so a message
 * never evaluates a half-set state. Trigger values must be strings.
 * @param {{ rating: number, reviewsGiven?: number }} params `reviewsGiven` omitted when unknown
 */
export const oneSignalSetReviewTriggers = ({ rating, reviewsGiven }) => {
  if (!isCordova() || !window?.plugins?.OneSignal?.InAppMessages) return;
  try {
    const triggers = { review_submitted: 'true', review_rating: String(rating) };
    if (Number.isInteger(reviewsGiven) && reviewsGiven >= 0) {
      triggers.reviews_given = String(reviewsGiven);
    }
    window.plugins.OneSignal.InAppMessages.addTriggers(triggers);
  } catch (e) {
    logError(e, 'one-signal-review-triggers');
  }
};

export const oneSignalLogin = async (currentUser, currentUserHasListings) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  // Login can reach this point with currentUser still null when the post-login
  // fetchCurrentUser failed (VANSITE-FRONTEND-4KE) — no user, nothing to tag.
  if (!currentUser?.id?.uuid) return;

  loginIssuedFor = currentUser.id.uuid;
  window.plugins.OneSignal.login(currentUser.id.uuid);
  // Logging in switches to the user behind this external ID and drops what was sent to the
  // anonymous one, so the capability tags must go out again.
  resetSentCapabilityTags();
  flushCapabilityTags();
  resendConsentTags();
  const oldTags = await window.plugins.OneSignal.User.getTags();
  if (!oldTags || Object.keys(oldTags).length === 0 || oldTags?.registered !== true) {
    const { attributes } = currentUser;
    const { email, profile, stripeConnected } = attributes;
    const { firstName, lastName, protectedData } = profile;
    oneSignalAddTags({
      email: email,
      first_name: firstName,
      last_name: lastName,
      stripe_connected: stripeConnected,
      is_provider: currentUserHasListings,
      is_potential_provider: !currentUserHasListings && protectedData.approvedForHostIntent,
      receive_news: true,
      registered: true,
    });
  }
};

export const oneSignalLogout = () => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.logout();
  loginIssuedFor = null;
  resetSentCapabilityTags();
  // The next user must not inherit this one's bucket through a later flush.
  setDesiredCapabilityTags(null);
  // The consent decisions belong to the device and go to the new anonymous user.
  resendConsentTags();
};

/**
 * @param {object} newTags
 */
export const oneSignalAddTags = (newTags) => {
  if (!isCordova() || !window?.plugins?.OneSignal) return;
  window.plugins.OneSignal.User.addTags(newTags);
};

/**
 * Bumped when a new in-app message action ships, so audiences (tag) and triggers can target
 * `iam_actions >= n`. 1: buy-pass, 2: prompt:* and open:signup,
 * 3: prompt:review and open:store-review.
 */
export const IAM_ACTIONS_VERSION = 3;
const IAM_ACTIONS_TRIGGER = 'iam_actions';

/**
 * `pass_type` values. The server writes the same tag (server/util/passAttributes.js,
 * PASS_TYPE_TAG_VALUES) — both writers must stay in lock-step, or the tag flips on every app open.
 */
export const PASS_TYPE_TAG_VALUES = {
  annual: 'jahrespass_stellplatz',
  family: 'jahrespass_familie',
  camping: 'jahrespass_camping',
  campingFamily: 'jahrespass_camping_familie',
  bundle: 'jahrespass_bundle',
  bundleFamily: 'jahrespass_bundle_familie',
  twentyFive: '25pass',
  flex: 'flexpass',
  none: 'none',
};

const FAMILY_GUEST_COUNT = 5;
const isTrueFlag = (value) => value === true || value === 'true';
const isFalseFlag = (value) => value === false || value === 'false';

// Mirrors variantPassTypeTag in server/util/passAttributes.js: `campingPass` adds camping,
// only an explicit `naturePass: false` drops the pitches.
const variantPassTypeTag = (annualPass) => {
  const isFamily = annualPass.included?.guestCount === FAMILY_GUEST_COUNT;
  const { annual, family, camping, campingFamily, bundle, bundleFamily } = PASS_TYPE_TAG_VALUES;
  if (!isTrueFlag(annualPass.campingPass)) return isFamily ? family : annual;
  if (isFalseFlag(annualPass.naturePass)) return isFamily ? campingFamily : camping;
  return isFamily ? bundleFamily : bundle;
};

/**
 * Pass type for IAM audiences. Same precedence as the server: FlexPass, then 25Pass, then the
 * annual pass variant; `none` without a valid pass.
 *
 * @param {object|null} currentUser
 * @returns {string} one of PASS_TYPE_TAG_VALUES
 */
export const passTypeTagOf = (currentUser) => {
  if (!hasCurrentUserValidAnnualPass(currentUser)) return PASS_TYPE_TAG_VALUES.none;
  const annualPass = currentUser.attributes.profile.privateData.annualPass;
  if (isFlexPass(annualPass)) return PASS_TYPE_TAG_VALUES.flex;
  if (annualPass.twentyFivePass) return PASS_TYPE_TAG_VALUES.twentyFive;
  return variantPassTypeTag(annualPass);
};

// The SDK drops tags sent before consent, so the wanted value is kept until the
// consent grant flushes it; the last sent value keeps unchanged tags from being resent.
let desiredCapabilityTags = null;
let desiredTagsUserId = null;
let lastSentCapabilityTags = null;
// Set by oneSignalLogin: native calls run in order, so tags sent after our own login(userId)
// land on that user even before the native SDK reports the new external ID.
let loginIssuedFor = null;

const resetSentCapabilityTags = () => {
  lastSentCapabilityTags = null;
};

const sameTags = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// fetchCurrentUser flushes before oneSignalLogin ran (and signup/session expiry can leave
// another user logged in natively): tags of user B must never land on user A. Guests have no
// ID to compare. The login-time flush sends what was held back.
const isNativeUserCurrent = async (userId) => {
  if (!userId || loginIssuedFor === userId) return true;
  try {
    return (await window.plugins.OneSignal.User.getExternalId()) === userId;
  } catch (e) {
    return false;
  }
};

const canFlushCapabilityTags = () =>
  desiredCapabilityTags &&
  oneSignalConsentGranted &&
  isCordova() &&
  window?.plugins?.OneSignal &&
  !sameTags(desiredCapabilityTags, lastSentCapabilityTags);

export const flushCapabilityTags = async () => {
  if (!canFlushCapabilityTags()) return;
  const tags = desiredCapabilityTags;
  if (!(await isNativeUserCurrent(desiredTagsUserId))) return;
  // The state may have moved on while the external ID was read.
  if (tags !== desiredCapabilityTags || !canFlushCapabilityTags()) return;
  try {
    oneSignalAddTags(tags);
    lastSentCapabilityTags = tags;
  } catch (e) {
    logError(e, 'one-signal-capability-tags');
  }
};

const setDesiredCapabilityTags = (currentUser) => {
  const userId = currentUser?.id?.uuid || null;
  const abBucket = computeAbBucket(userId);
  desiredTagsUserId = userId;
  desiredCapabilityTags = {
    iam_actions: IAM_ACTIONS_VERSION,
    pass_type: passTypeTagOf(currentUser),
    ...(abBucket === null ? {} : { ab_bucket: abBucket }),
  };
};

/**
 * Tags the app's IAM capability, the user's pass type and A/B bucket (0-99, 10-10-80 in-app
 * message tests) for audience targeting. Guests get no bucket; after logout the tag stays on
 * the OneSignal user (keyed by external_id) and is never removed.
 *
 * @param {object|null} currentUser
 */
export const oneSignalSyncCapabilityTags = (currentUser) => {
  if (!isCordova()) return;
  // Stored even before the plugin is attached (fetchCurrentUser can run before deviceready);
  // the flush after the consent grant sends it.
  setDesiredCapabilityTags(currentUser);
  flushCapabilityTags();
};

// The device's consent decisions: tags for segments, and the same keys as triggers, because
// audiences are evaluated at session start, too late for the onboarding message that follows the
// cookie decision in the first session. Tags are resent once per app start, then only on change.
let lastSentConsentTags = null;
// Last ATT status read. Read ahead at init, so that in the first session the grant (after the
// startup screens) can trigger it together with `marketing_consent`, before `iam_actions`.
let knownAttStatus = null;

// Android has no ATT, and its idfa plugin reads the Play Services ad ID instead.
const refreshAttStatus = async () => {
  if (isIOS()) knownAttStatus = await getTrackingStatus();
};

// Without a stored decision (expired consent) nothing is sent: undecided is not declined.
const canSyncConsentTags = () =>
  oneSignalConsentGranted && isCordova() && !!window?.plugins?.OneSignal && !!getConsent();

const currentConsentTags = () => ({
  marketing_consent: String(!!getConsent()?.marketing),
  ...(knownAttStatus ? { att_status: knownAttStatus } : {}),
});

const setConsentTriggers = (tags) =>
  Object.entries(tags).forEach(([key, value]) => oneSignalAddTrigger(key, value));

const sendConsentTags = (tags) => {
  if (sameTags(tags, lastSentConsentTags)) return;
  oneSignalAddTags({ ...tags });
  lastSentConsentTags = tags;
};

/**
 * Tags and triggers `marketing_consent` (`true`/`false`) and, on iOS, `att_status`
 * (`authorized`/`denied`/`not_determined`). Runs after the OneSignal consent grant, on every
 * cookie-settings change, after the ATT dialog, on login/logout and on resume (ATT can change in
 * the iOS settings). Never rejects.
 */
export const syncConsentTags = async () => {
  if (!canSyncConsentTags()) return;
  try {
    // Before anything awaits: at the grant the `iam_actions` trigger follows right after this call,
    // and no message may be picked on missing values.
    setConsentTriggers(currentConsentTags());
    await refreshAttStatus();
    // Read again: a newer sync may have run meanwhile, and an older state must not win.
    if (!canSyncConsentTags()) return;
    const tags = currentConsentTags();
    setConsentTriggers(tags);
    sendConsentTags(tags);
  } catch (e) {
    logError(e, 'one-signal-consent-tags');
  }
};

const resendConsentTags = () => {
  lastSentConsentTags = null;
  syncConsentTags();
};
